import React, { useEffect, useState } from 'react';
import { Loader2, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { errMsg } from '@/services/staffAttendanceApi';
import { directShipApi } from '@/services/ewayBillApi';
import { DocUploadButton } from '@/components/ewayBill/DocUploadButton';
import { ShipBoxesDialog } from '@/components/ewayBill/ShipBoxesDialog';

const STATE = {
  no_boxes: ['Tick boxes', 'bg-amber-100 text-amber-800'],
  mismatch: ["Pieces don't match", 'bg-rose-100 text-rose-800'],
  ok: ['Pieces match', 'bg-emerald-100 text-emerald-800'],
};
const ewbText = (b) => (b.ewb_no ? `EWB ${b.ewb_no}` : `E-way bill ${b.status.replace('_', ' ').toLowerCase()}`);

const ToRow = ({ t, onBoxes, onUse, onRemove }) => {
  const [label, cls] = STATE[t.state];
  return (
    <li className="space-y-1 px-3 py-2 text-sm" data-testid={`ds-to-row-${t.doc_no}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono font-semibold">{t.doc_no}</span>
        <span className="truncate text-gray-600">{t.party}{t.place ? ` · ${t.place}` : ''}</span>
        <span className="text-xs text-gray-500">{t.doc_date} · {t.qty} pcs · Challan {t.challan_no || '—'}</span>
        <span className={`rounded-full px-2 text-[11px] font-semibold ${cls}`} data-testid={`ds-to-state-${t.doc_no}`}>{label}</span>
        {t.ewb && <span className="rounded-full bg-emerald-100 px-2 text-[11px] font-semibold text-emerald-800" data-testid={`ds-to-ewb-${t.doc_no}`}>{ewbText(t.ewb)}</span>}
        {!t.ewb && (
          <div className="ml-auto flex gap-1">
            <Button size="sm" variant="outline" className="h-8" onClick={() => onBoxes(t)} data-testid={`ds-to-boxes-btn-${t.doc_no}`}>{t.box_numbers.length ? 'Change boxes' : 'Tick boxes'}</Button>
            {onUse && t.state === 'ok' && <Button size="sm" className="h-8 bg-[#2D5F4F] hover:bg-[#234A3D]" onClick={() => onUse(t)} data-testid={`ds-to-use-btn-${t.doc_no}`}>Make e-way bill</Button>}
            <Button size="sm" variant="ghost" className="h-8 text-rose-600" onClick={() => onRemove(t)} data-testid={`ds-to-remove-btn-${t.doc_no}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        )}
      </div>
      {t.box_numbers.length > 0 && (
        <div className="text-xs text-gray-600" data-testid={`ds-to-boxes-${t.doc_no}`}>{t.box_numbers.length} box(es) → <b>{t.to_site}</b> · {t.box_pcs} pcs: <span className="font-mono">{t.box_numbers.join(', ')}</span></div>
      )}
      {t.problems.map((p) => <div key={p} className="text-xs text-rose-700">{p}</div>)}
    </li>
  );
};

/** Direct Ship: upload the Ginesys Transfer Out made for the shipment, tick its boxes (one site, pieces must match). `onUse` → make the e-way bill. */
export const DirectShipToPanel = ({ onUse, onCancel }) => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState('');
  const [tick, setTick] = useState(0);
  const [boxTo, setBoxTo] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => directShipApi.list(q).then(setRows).catch((e) => { setRows([]); toast.error(errMsg(e, 'Could not load the Transfer Outs')); }), 300);
    return () => clearTimeout(t);
  }, [q, tick]);
  const reload = () => setTick((n) => n + 1);
  const remove = (t) => {
    if (!window.confirm(`Remove Transfer Out ${t.doc_no}? Its boxes go back to the list.`)) return;
    directShipApi.remove(t.id).then(() => { toast.success(`${t.doc_no} removed`); reload(); }).catch((e) => toast.error(errMsg(e, 'Could not remove it')));
  };
  return (
    <div className="space-y-3" data-testid="ds-to-panel">
      <div className="flex flex-wrap items-start justify-between gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2">
        <p className="max-w-xl pt-1.5 text-xs text-gray-600">Upload the <b>Ginesys Transfer Out</b> made for the Direct Ship, then tick the boxes that went with it (one site). WMS checks the pieces item by item. The e-way bill is generated by hand — never on its own.</p>
        <DocUploadButton kind="direct_ship" onDone={reload} />
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Transfer Out / Challan / box / site" className="pl-9" data-testid="ds-to-search" />
      </div>
      <ul className="max-h-[50vh] divide-y overflow-y-auto rounded-lg border border-gray-200 bg-white" data-testid="ds-to-list">
        {rows === null ? <Loader2 className="w-5 h-5 mx-auto my-6 animate-spin text-amber-600" />
          : rows.length === 0 ? <li className="py-8 text-center text-sm text-gray-500" data-testid="ds-to-empty">No Direct Ship Transfer Out uploaded yet</li>
            : rows.map((t) => <ToRow key={t.id} t={t} onBoxes={setBoxTo} onUse={onUse} onRemove={remove} />)}
      </ul>
      {onCancel && <Button variant="ghost" onClick={onCancel} data-testid="ds-cancel-btn">Back</Button>}
      <ShipBoxesDialog to={boxTo} onClose={() => setBoxTo(null)} onSaved={() => { setBoxTo(null); reload(); }} />
    </div>
  );
};

export default DirectShipToPanel;
